import {
  FileText,
  Users2,
  TrendingUp,
  CalendarCheck,
  CreditCard,
  FileSignature,
  Plug,
  Gift,
  MessageCircle,
  Lightbulb,
  Activity,
  Check,
  Star,
  ArrowUpRight,
  Circle,
  Calendar,
  Wallet,
  Sparkles,
  Brain,
  MapPin,
  Receipt,
  type LucideIcon,
} from "lucide-react";

interface Props {
  slug: string;
}

const Frame = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <div className="rounded-2xl border border-border/60 bg-card shadow-lg shadow-lime/5 overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/40">
      <div className="flex gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-lime/80" />
      </div>
      <span className="ml-2 text-xs text-muted-foreground font-medium">{title}</span>
    </div>
    <div className="p-4 md:p-5">{children}</div>
  </div>
);

const Bar = ({ w, tone = "lime" }: { w: string; tone?: "lime" | "muted" }) => (
  <div
    className={`h-2 rounded-full ${tone === "lime" ? "bg-lime" : "bg-muted-foreground/20"}`}
    style={{ width: w }}
  />
);

const Row = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-border/40 last:border-0">
    {children}
  </div>
);

export const SolucaoMockScreen = ({ slug }: Props) => {
  switch (slug) {
    case "proposta-digital":
      return (
        <>

          <style>{`
            @keyframes ameup-scene {
              0%, 42%   { opacity: 1; transform: translateY(0); }
              48%, 96%  { opacity: 0; transform: translateY(-6px); pointer-events: none; }
              100%      { opacity: 1; transform: translateY(0); }
            }
            @keyframes ameup-scene-2 {
              0%, 42%   { opacity: 0; transform: translateY(6px); pointer-events: none; }
              50%, 94%  { opacity: 1; transform: translateY(0); }
              100%      { opacity: 0; transform: translateY(6px); }
            }
            @keyframes ameup-row {
              0%, 5%   { opacity: 0; transform: translateX(-6px); }
              15%,100% { opacity: 1; transform: translateX(0); }
            }
            @keyframes ameup-total {
              0%, 32%  { opacity: 0; transform: scale(0.96); }
              40%,100% { opacity: 1; transform: scale(1); }
            }
            @keyframes ameup-send-btn {
              0%, 34%   { transform: scale(1); box-shadow: 0 0 0 0 hsl(var(--lime) / 0.5); }
              38%       { transform: scale(0.94); box-shadow: 0 0 0 8px hsl(var(--lime) / 0); }
              42%,100%  { transform: scale(1); }
            }
            @keyframes ameup-bubble-in {
              0%, 55%  { opacity: 0; transform: translateY(10px) scale(0.96); }
              62%,100% { opacity: 1; transform: translateY(0) scale(1); }
            }
            @keyframes ameup-ticks {
              0%, 78%  { color: rgba(255,255,255,0.5); }
              86%,100% { color: hsl(var(--lime)); }
            }
            @keyframes ameup-live {
              0%,100% { opacity: 0.4; }
              50%     { opacity: 1; }
            }
          `}</style>

          <div className="relative mx-auto" style={{ width: "260px", height: "520px" }}>
            {/* Phone frame */}
            <div className="absolute inset-0 rounded-[36px] bg-neutral-900 shadow-2xl shadow-lime/10 p-2">
              <div className="relative w-full h-full rounded-[28px] bg-background overflow-hidden">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-neutral-900 rounded-b-2xl z-20" />

                {/* Status bar */}
                <div className="absolute top-0 left-0 right-0 h-8 flex items-center justify-between px-5 text-[9px] font-semibold text-foreground z-10">
                  <span>14:22</span>
                  <span className="flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-lime" />
                    <span
                      className="text-[8px] text-muted-foreground"
                      style={{ animation: "ameup-live 1.8s ease-in-out infinite" }}
                    >
                      ao vivo
                    </span>
                  </span>
                </div>

                {/* SCENE 1 — Building the proposal */}
                <div
                  className="absolute inset-0 pt-9 px-3 pb-3 flex flex-col"
                  style={{ animation: "ameup-scene 9s ease-in-out infinite" }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-[8px] text-muted-foreground uppercase tracking-wide">Nova proposta</p>
                      <p className="text-[11px] font-bold text-foreground">Ana & Léo · Casamento</p>
                    </div>
                    <span className="px-1.5 py-0.5 rounded-full bg-lime/15 text-lime text-[8px] font-semibold">
                      120 conv.
                    </span>
                  </div>

                  <div className="flex-1 rounded-2xl bg-muted/30 border border-border/60 p-2.5 space-y-1.5 overflow-hidden">
                    <p className="text-[8px] text-muted-foreground uppercase tracking-wide mb-1">Cardápio Diamante</p>
                    {[
                      { l: "Bruschettas trio", v: "incluso", d: "0.2s" },
                      { l: "Filé ao molho madeira", v: "incluso", d: "0.7s" },
                      { l: "Risoto de camarão", v: "+R$ 1.200", d: "1.2s" },
                      { l: "Open bar premium 5h", v: "+R$ 2.400", d: "1.7s" },
                      { l: "DJ + iluminação", v: "+R$ 1.800", d: "2.2s" },
                      { l: "Cupom NOIVOS10", v: "-R$ 1.890", d: "2.7s" },
                    ].map((r) => (
                      <div
                        key={r.l}
                        className="flex items-center justify-between text-[9px] py-1 border-b border-border/40 last:border-0"
                        style={{ animation: `ameup-row 9s ease-out ${r.d} infinite both` }}
                      >
                        <span className="text-muted-foreground truncate pr-2">{r.l}</span>
                        <span className={`font-semibold ${r.v.startsWith("-") ? "text-lime" : "text-foreground"}`}>
                          {r.v}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div
                    className="mt-2 rounded-xl bg-foreground text-background px-3 py-2 flex items-center justify-between"
                    style={{ animation: "ameup-total 9s ease-out infinite both" }}
                  >
                    <span className="text-[9px] opacity-70">Total</span>
                    <span className="text-sm font-bold">R$ 18.900</span>
                  </div>

                  <button
                    className="mt-2 rounded-xl bg-lime text-neutral-900 py-2 text-[11px] font-bold flex items-center justify-center gap-1.5"
                    style={{ animation: "ameup-send-btn 9s ease-out infinite" }}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    Enviar por WhatsApp
                  </button>
                </div>

                {/* SCENE 2 — WhatsApp chat with link card */}
                <div
                  className="absolute inset-0 pt-8 flex flex-col bg-[#0b141a]"
                  style={{ animation: "ameup-scene-2 9s ease-in-out infinite" }}
                >
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-[#1f2c33]">
                    <div className="w-7 h-7 rounded-full bg-lime/80 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-[#0b141a]">AB</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-semibold text-white truncate">Ana Beatriz</p>
                      <p className="text-[8px] text-white/60">online</p>
                    </div>
                  </div>

                  <div
                    className="flex-1 p-2.5 flex flex-col justify-end gap-2"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.02) 1px, transparent 1px)",
                      backgroundSize: "18px 18px",
                    }}
                  >
                    <div className="self-start rounded-lg rounded-bl-none bg-white/10 px-2.5 py-1.5 max-w-[80%]">
                      <p className="text-[10px] text-white/90">Manda a proposta? 🙏</p>
                    </div>

                    <div
                      className="self-end max-w-[85%] origin-bottom-right"
                      style={{ animation: "ameup-bubble-in 9s ease-out infinite both" }}
                    >
                      <div className="rounded-lg rounded-br-none bg-[#005c4b] p-1.5 space-y-1">
                        <div className="rounded-md bg-white/95 p-2 space-y-0.5">
                          <p className="text-[9px] font-semibold text-foreground leading-tight">
                            Cardápio Diamante
                          </p>
                          <p className="text-[8px] text-muted-foreground">120 convidados · casamento</p>
                          <div className="flex items-center justify-between pt-1 mt-1 border-t border-border/40">
                            <span className="text-[8px] text-muted-foreground">Total</span>
                            <span className="text-[10px] font-bold text-foreground">R$ 18.900</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between px-1 pt-0.5">
                          <span className="text-[8px] text-white/70 truncate">ameup.com.br/p/4821</span>
                          <span className="flex items-center gap-1">
                            <span className="text-[7px] text-white/60">14:22</span>
                            <span
                              className="inline-flex"
                              style={{ animation: "ameup-ticks 9s ease-out infinite" }}
                            >
                              <Check className="w-2.5 h-2.5 -mr-1.5" />
                              <Check className="w-2.5 h-2.5" />
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>

      );



    case "crm": {
      const columns = [
        {
          t: "Simulação",
          n: 12,
          tone: "muted" as const,
          cards: [
            { name: "Aniversário Marina", date: "22/03", value: "R$ 4.8k" },
            { name: "Casamento Ana", date: "05/04", value: "R$ 12k" },
            { name: "Corporativo Nexon", date: "18/04", value: "R$ 8.2k" },
            { name: "15 anos Júlia", date: "02/05", value: "R$ 6.5k" },
          ],
        },
        {
          t: "Programado",
          n: 7,
          tone: "lime" as const,
          cards: [
            { name: "Casamento L&R", date: "12/03", value: "R$ 15k" },
            { name: "Aniversário Pedro", date: "20/03", value: "R$ 3.9k" },
            { name: "Corporativo Vertex", date: "28/03", value: "R$ 9.1k" },
          ],
        },
        {
          t: "Em Preparação",
          n: 4,
          tone: "lime" as const,
          cards: [
            { name: "Batizado Sofia", date: "09/03", value: "R$ 5.2k" },
            { name: "Corporativo TechDay", date: "11/03", value: "R$ 11k" },
            { name: "Aniversário Rafael", date: "14/03", value: "R$ 4.1k" },
          ],
        },
        {
          t: "Entregue",
          n: 3,
          tone: "lime" as const,
          cards: [
            { name: "Casamento M&J", date: "01/03", value: "R$ 14k" },
            { name: "Corporativo Delta", date: "03/03", value: "R$ 7.8k" },
            { name: "Aniversário Beatriz", date: "05/03", value: "R$ 3.6k" },
          ],
        },
      ];

      return (
        <Frame title="CRM · Kanban">
          <style>{`
            @keyframes crm-fly {
              0%, 8%   { transform: translate(0, 0) scale(1) rotate(0deg); opacity: 0; }
              14%      { opacity: 1; }
              25%      { transform: translate(0, -8px) scale(1.04) rotate(-2deg); opacity: 1; }
              55%      { transform: translate(105%, -4px) scale(1.04) rotate(1deg); opacity: 1; }
              72%      { transform: translate(105%, 0) scale(1) rotate(0deg); opacity: 1; }
              82%,100% { transform: translate(105%, 0) scale(1) rotate(0deg); opacity: 0; }
            }
            @keyframes crm-source-fade {
              0%, 20%   { opacity: 1; }
              22%, 70%  { opacity: 0.15; }
              78%, 100% { opacity: 1; }
            }
            @keyframes crm-target-bump {
              0%, 60%   { transform: translateY(0); }
              70%       { transform: translateY(-4px); }
              80%, 100% { transform: translateY(0); }
            }
            @keyframes crm-counter-src {
              0%, 60%   { opacity: 1; }
              70%, 100% { opacity: 0.5; }
            }
          `}</style>
          <div className="relative grid grid-cols-4 gap-3">
            {columns.map((c, colIdx) => (
              <div key={c.t} className="rounded-xl bg-muted/40 p-2.5 space-y-2 min-h-[240px]">
                <div className="flex items-center justify-between px-0.5">
                  <span className="text-[9px] font-semibold text-foreground/80 uppercase tracking-wide">
                    {c.t}
                  </span>
                  <span
                    className={`text-[9px] font-bold ${c.tone === "lime" ? "text-lime" : "text-muted-foreground"}`}
                    style={
                      colIdx === 1
                        ? { animation: "crm-counter-src 4.2s ease-in-out infinite" }
                        : undefined
                    }
                  >
                    {c.n}
                  </span>
                </div>
                <div
                  className="space-y-2"
                  style={
                    colIdx === 2
                      ? { animation: "crm-target-bump 4.2s ease-in-out infinite" }
                      : undefined
                  }
                >
                  {c.cards.map((card, i) => (
                    <div
                      key={i}
                      className="rounded-lg bg-background p-2.5 space-y-1.5 shadow-sm border border-border/40"
                      style={
                        colIdx === 1 && i === 0
                          ? { animation: "crm-source-fade 4.2s ease-in-out infinite" }
                          : undefined
                      }
                    >
                      <div className="space-y-0.5">
                        <span className="block text-[10px] leading-tight font-semibold text-foreground">
                          {card.name}
                        </span>
                        <span className="block text-[9px] text-muted-foreground">{card.date}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Bar w="60%" tone={c.tone} />
                        <span className={`text-[9px] font-bold ${c.tone === "lime" ? "text-lime" : "text-muted-foreground"}`}>
                          {card.value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Flying card animation: Programado -> Em Preparação */}
            <div
              className="absolute pointer-events-none"
              style={{
                top: "34px",
                left: "calc(25% + 10px)",
                width: "calc(25% - 20px)",
                animation: "crm-fly 4.2s ease-in-out infinite",
              }}
            >
              <div className="rounded-lg bg-background p-2.5 space-y-1.5 shadow-xl border-2 border-lime/60 ring-2 ring-lime/20">
                <div className="space-y-0.5">
                  <span className="block text-[10px] leading-tight font-semibold text-foreground">
                    Casamento L&R
                  </span>
                  <span className="block text-[9px] text-muted-foreground">12/03</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Bar w="60%" tone="lime" />
                  <span className="text-[9px] font-bold text-lime">R$ 15k</span>
                </div>
              </div>
            </div>
          </div>
        </Frame>
      );
    }


    case "funil-de-vendas":
      return (
        <Frame title="Funil de Vendas">
          <div className="space-y-3">
            {[
              { l: "Acessou Proposta", v: "100%", w: "100%" },
              { l: "Preencheu Simulador", v: "68%", w: "68%" },
              { l: "Escolheu Itens", v: "45%", w: "45%" },
              { l: "Detalhes da Festa", v: "28%", w: "28%" },
              { l: "Aguardando Pagamento", v: "16%", w: "16%" },
              { l: "Programado", v: "9%", w: "9%" },
            ].map((s) => (
              <div key={s.l}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{s.l}</span>
                  <span className="font-semibold text-lime">{s.v}</span>
                </div>
                <div className="h-6 rounded-md bg-muted/40 overflow-hidden">
                  <div className="h-full bg-lime/80" style={{ width: s.w }} />
                </div>
              </div>
            ))}
          </div>
        </Frame>
      );

    case "gestao-do-evento":
      return (
        <Frame title="Evento · Sábado 15/03">
          <div className="space-y-2">
            {[
              { i: CalendarCheck, l: "Data confirmada", v: "15/03 · 19h" },
              { i: Users2, l: "Convidados", v: "120" },
              { i: FileSignature, l: "Contrato", v: "Assinado" },
              { i: CreditCard, l: "Pagamento", v: "60% pago" },
            ].map((r) => (
              <div key={r.l} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/40">
                <r.i className="w-4 h-4 text-lime" />
                <span className="text-sm text-foreground flex-1">{r.l}</span>
                <span className="text-sm font-semibold text-foreground">{r.v}</span>
              </div>
            ))}
          </div>
        </Frame>
      );

    case "pagamento-integrado":
      return (
        <>
          <style>{`
            @keyframes pay-approve {
              0%, 62%   { opacity: 0; transform: scale(0.96); pointer-events: none; }
              70%, 94%  { opacity: 1; transform: scale(1); }
              100%      { opacity: 0; transform: scale(0.96); }
            }
            @keyframes pay-checkout {
              0%, 60%   { opacity: 1; }
              70%, 96%  { opacity: 0; }
              100%      { opacity: 1; }
            }
            @keyframes pay-btn {
              0%, 48%   { transform: scale(1); box-shadow: 0 0 0 0 hsl(var(--lime) / 0.5); }
              54%       { transform: scale(0.94); box-shadow: 0 0 0 10px hsl(var(--lime) / 0); }
              60%, 100% { transform: scale(1); }
            }
          `}</style>

          <div className="relative mx-auto" style={{ width: "260px", height: "520px" }}>
            <div className="absolute inset-0 rounded-[36px] bg-neutral-900 shadow-2xl shadow-lime/10 p-2">
              <div className="relative w-full h-full rounded-[28px] bg-background overflow-hidden">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-neutral-900 rounded-b-2xl z-20" />

                {/* Status bar */}
                <div className="absolute top-0 left-0 right-0 h-8 flex items-center justify-between px-5 text-[9px] font-semibold text-foreground z-10">
                  <span>14:22</span>
                  <span className="text-[8px] text-muted-foreground">pagamento seguro</span>
                </div>

                {/* Checkout screen */}
                <div
                  className="absolute inset-0 pt-9 px-3.5 pb-3.5 flex flex-col"
                  style={{ animation: "pay-checkout 6s ease-in-out infinite" }}
                >
                  <div className="flex items-center gap-2 pb-2.5 border-b border-border/60">
                    <div className="w-7 h-7 rounded-lg bg-lime/15 flex items-center justify-center">
                      <CreditCard className="w-3.5 h-3.5 text-lime" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-foreground leading-tight">
                        Chef Eventos Buffet
                      </p>
                      <p className="text-[8px] text-muted-foreground leading-tight">
                        Aniversário Marina · 22/03
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-xl border border-border/60 p-3">
                    <p className="text-[8px] text-muted-foreground uppercase tracking-wide">
                      Total do evento
                    </p>
                    <p className="text-xl font-bold text-foreground leading-tight">R$ 5.200,00</p>
                    <p className="text-[8px] text-muted-foreground mt-1">
                      Sinal de 30% hoje · saldo até 15/03
                    </p>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="rounded-lg border-2 border-lime bg-lime/10 px-2 py-2 text-center">
                      <p className="text-[9px] font-bold text-foreground">Pix</p>
                      <p className="text-[7px] text-lime font-semibold">5% off</p>
                    </div>
                    <div className="rounded-lg border border-border/60 px-2 py-2 text-center">
                      <p className="text-[9px] font-semibold text-muted-foreground">Cartão</p>
                      <p className="text-[7px] text-muted-foreground">até 12x</p>
                    </div>
                  </div>

                  <div className="mt-3 flex-1 rounded-xl bg-muted/40 border border-border/50 flex flex-col items-center justify-center gap-2">
                    <div className="w-20 h-20 rounded-lg bg-background border border-border/60 grid grid-cols-5 grid-rows-5 gap-[2px] p-1.5">
                      {Array.from({ length: 25 }).map((_, i) => (
                        <span
                          key={i}
                          className={`rounded-[1px] ${[0, 1, 3, 4, 6, 8, 10, 12, 13, 16, 18, 19, 21, 23, 24].includes(i) ? "bg-foreground" : "bg-transparent"}`}
                        />
                      ))}
                    </div>
                    <p className="text-[8px] text-muted-foreground">QR Code Pix · expira em 30 min</p>
                  </div>

                  <div
                    className="mt-3 rounded-xl bg-lime py-2.5 text-center"
                    style={{ animation: "pay-btn 6s ease-in-out infinite" }}
                  >
                    <span className="text-[11px] font-bold text-neutral-900">
                      Pagar R$ 1.560,00
                    </span>
                  </div>
                </div>

                {/* Approved screen */}
                <div
                  className="absolute inset-0 pt-9 px-4 pb-4 flex flex-col items-center justify-center text-center bg-background"
                  style={{ animation: "pay-approve 6s ease-in-out infinite" }}
                >
                  <div className="w-14 h-14 rounded-full bg-lime/15 flex items-center justify-center">
                    <Check className="w-7 h-7 text-lime" />
                  </div>
                  <p className="mt-3 text-sm font-bold text-foreground">Pagamento aprovado</p>
                  <p className="text-[9px] text-muted-foreground mt-1">
                    Pix confirmado em 3 segundos
                  </p>
                  <div className="mt-4 w-full rounded-xl border border-border/60 p-3 space-y-2 text-left">
                    {[
                      { l: "Valor pago", v: "R$ 1.560,00" },
                      { l: "Split fornecedor", v: "R$ 1.482,00" },
                      { l: "Contrato", v: "Liberado" },
                    ].map((r) => (
                      <div key={r.l} className="flex items-center justify-between">
                        <span className="text-[9px] text-muted-foreground">{r.l}</span>
                        <span className="text-[9px] font-semibold text-foreground">{r.v}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-[8px] text-muted-foreground">
                    Comprovante enviado no WhatsApp
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      );


    case "contrato-automatico":
      return (
        <Frame title="Contrato · Assinatura">
          <div className="space-y-3">
            <div className="rounded-lg border border-border/60 p-3 bg-muted/30 space-y-1.5">
              <Bar w="90%" tone="muted" />
              <Bar w="75%" tone="muted" />
              <Bar w="82%" tone="muted" />
              <Bar w="60%" tone="muted" />
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-lime/10 border border-lime/30">
              <FileSignature className="w-5 h-5 text-lime" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Assinado digitalmente</p>
                <p className="text-xs text-muted-foreground">Cliente · 15/03 às 14:22</p>
              </div>
              <Check className="w-4 h-4 text-lime" />
            </div>
          </div>
        </Frame>
      );

    case "integracoes": {
      const SB = "https://qfozcnxbtjjmidhjdepc.supabase.co/storage/v1/object/public/Photos_Home";
      const integrations: { name: string; src: string }[] = [
        { name: "WhatsApp", src: "https://cdn.simpleicons.org/whatsapp/25D366" },
        { name: "Google Calendar", src: `${SB}/Google-Calendar-Logo.png` },
        { name: "Google Maps", src: `${SB}/Google%20Maps.jpeg` },
        { name: "PagBank", src: `${SB}/Pagbank.png` },
        { name: "Stripe", src: "https://cdn.simpleicons.org/stripe/635BFF" },
        { name: "Gemini", src: `${SB}/Gemi.jpeg` },
        { name: "Claude", src: `${SB}/Claude.webp` },
        
      ];
      return (
        <Frame title="Integrações ativas">
          <div className="grid grid-cols-2 gap-2">
            {integrations.map(({ name, src }) => {
              const big = ["Google Maps", "PagBank", "Google Calendar"].includes(name);
              return (
              <div key={name} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-muted/40">
                <span className={`${big ? "w-9 h-9" : "w-7 h-7"} rounded-md bg-background flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                  <img
                    src={src}
                    alt={name}
                    className={`${big ? "w-8 h-8" : "w-5 h-5"} object-contain`}
                    loading="lazy"
                  />
                </span>
                <span className="text-sm text-foreground flex-1 truncate">{name}</span>
                <span className="text-[10px] font-semibold text-lime">ON</span>
              </div>
              );
            })}
          </div>
        </Frame>
      );
    }

    case "programa-de-indicacao":
      return (
        <Frame title="Programa de Indicação">
          <div className="space-y-3">
            <div className="rounded-lg bg-lime/10 border border-lime/30 p-3 text-center">
              <Gift className="w-6 h-6 text-lime mx-auto mb-1" />
              <p className="text-2xl font-bold text-foreground">12</p>
              <p className="text-xs text-muted-foreground">indicações convertidas</p>
            </div>
            <div className="rounded-lg bg-muted/40 p-3">
              <p className="text-xs text-muted-foreground mb-1">Seu link único</p>
              <p className="text-sm font-mono text-foreground truncate">ameup.com.br/i/joao42</p>
            </div>
          </div>
        </Frame>
      );

    case "feedback-whatsapp":
      return (
        <Frame title="Feedback · WhatsApp">
          <style>{`
            @keyframes fb1 { 0%,2% {opacity:0; transform:translateY(10px);} 8%,96% {opacity:1; transform:translateY(0);} 100% {opacity:0; transform:translateY(10px);} }
            @keyframes fb2 { 0%,32% {opacity:0; transform:translateY(10px);} 38%,96% {opacity:1; transform:translateY(0);} 100% {opacity:0; transform:translateY(10px);} }
            @keyframes fb3 { 0%,62% {opacity:0; transform:translateY(10px);} 68%,96% {opacity:1; transform:translateY(0);} 100% {opacity:0; transform:translateY(10px);} }
            @keyframes fbStar { 0%,42% {opacity:0; transform:scale(.4);} 50%,96% {opacity:1; transform:scale(1);} 100% {opacity:0; transform:scale(.4);} }
            .fb-1 { animation: fb1 10s ease-in-out infinite; opacity:0; }
            .fb-2 { animation: fb2 10s ease-in-out infinite; opacity:0; }
            .fb-3 { animation: fb3 10s ease-in-out infinite; opacity:0; }
            .fb-s { animation: fbStar 10s ease-in-out infinite; opacity:0; }
            @media (prefers-reduced-motion: reduce) {
              .fb-1, .fb-2, .fb-3, .fb-s { animation: none; opacity: 1; }
            }
          `}</style>
          <div className="space-y-2.5">
            <div className="fb-1 rounded-2xl bg-lime/10 p-3 max-w-[92%]">
              <p className="text-sm font-semibold text-foreground">Avaliação</p>
              <p className="text-sm text-foreground mt-1">
                Olá, Marina 😊 Foi um prazer fazer parte do seu evento! Gostaríamos de pedir sua
                avaliação. É bem rapidinho e sua opinião ajuda muito na evolução do nosso trabalho.
              </p>
              <p className="text-xs text-muted-foreground mt-2">Obrigado pela participação</p>
              <div className="mt-2 border-t border-border/60 pt-2 text-center text-sm font-medium text-foreground">
                Abrir opções
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 text-right">13:17</p>
            </div>
            <div className="fb-2 rounded-2xl bg-muted/50 p-3 max-w-[85%] ml-auto">
              <p className="text-xs font-semibold text-foreground">Avaliação</p>
              <div className="flex items-center gap-1 mt-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className="fb-s w-4 h-4 fill-lime text-lime"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
              <p className="text-sm text-foreground mt-1">5 · Excelente</p>
              <p className="text-[10px] text-muted-foreground mt-1 text-right">13:18 ✓✓</p>
            </div>
            <div className="fb-3 rounded-2xl bg-lime/10 p-3 max-w-[92%]">
              <p className="text-sm text-foreground">
                Muito obrigado pelo seu feedback! Nós da Chef Eventos agradecemos 🧡
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">13:19</p>
            </div>
          </div>



        </Frame>
      );

    case "insight-de-vendas":
      return (
        <Frame title="Insights de Vendas">
          {/* Insight do mês — mesmo padrão de Indicadores > Funil de Vendas */}
          <style>{`
            @keyframes insight-a { 0%,28% {opacity:1;} 33%,95% {opacity:0;} 100% {opacity:1;} }
            @keyframes insight-b { 0%,30% {opacity:0;} 35%,61% {opacity:1;} 66%,100% {opacity:0;} }
            @keyframes insight-c { 0%,63% {opacity:0;} 68%,95% {opacity:1;} 100% {opacity:0;} }
            @keyframes insight-dot1 { 0%,30% {width:16px;opacity:.6;} 33%,100% {width:4px;opacity:.18;} }
            @keyframes insight-dot2 { 0%,30% {width:4px;opacity:.18;} 35%,63% {width:16px;opacity:.6;} 66%,100% {width:4px;opacity:.18;} }
            @keyframes insight-dot3 { 0%,63% {width:4px;opacity:.18;} 68%,95% {width:16px;opacity:.6;} 100% {width:4px;opacity:.18;} }
          `}</style>
          <div className="relative mb-4 h-[104px]">
            {[
              {
                Icon: Wallet,
                title: "Valor em jogo neste mês",
                text: "Você tem R$ 1.502,00 em negociação. Foque nos clientes mais quentes de acordo com a etapa do funil que estão.",
                anim: "insight-a",
              },
              {
                Icon: TrendingUp,
                title: "Conversão acima da média",
                text: "9,4% das simulações viraram evento confirmado — 2,1 p.p. acima do mês anterior.",
                anim: "insight-b",
              },
              {
                Icon: Lightbulb,
                title: "Cardápio mais vendido",
                text: "O menu Diamante representa 38% das propostas fechadas. Destaque-o nas próximas propostas.",
                anim: "insight-c",
              },
            ].map(({ Icon, title, text, anim }) => (
              <div
                key={title}
                className="absolute inset-0 rounded-xl bg-background border border-border/60 px-4 py-3.5"
                style={{ animation: `${anim} 12s ease-in-out infinite` }}
              >
                <div className="flex items-start gap-3">
                  <div className="p-1.5 rounded-lg bg-muted/60 text-muted-foreground shrink-0">
                    <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 pr-10">
                    <p className="text-[9px] font-medium text-muted-foreground/80 uppercase tracking-wider">
                      Insight do mês
                    </p>
                    <p className="text-[11px] font-semibold text-foreground leading-tight mt-1">
                      {title}
                    </p>
                    <p className="text-[10px] text-muted-foreground leading-relaxed mt-1.5">
                      {text}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            <div className="absolute top-4 right-4 flex gap-1">
              {["insight-dot1", "insight-dot2", "insight-dot3"].map((d) => (
                <span
                  key={d}
                  className="h-1 rounded-full bg-foreground"
                  style={{ animation: `${d} 12s ease-in-out infinite` }}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-lg bg-muted/40 p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Ticket médio</p>
              <p className="text-lg font-bold text-foreground">R$ 14.2k</p>
            </div>
            <div className="rounded-lg bg-lime/10 border border-lime/30 p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Conversão</p>
              <p className="text-lg font-bold text-lime">9,4%</p>
            </div>
          </div>
          <div className="flex items-end gap-1.5 h-20">
            {[40, 55, 35, 70, 60, 85, 75].map((h, i) => (
              <div key={i} className="flex-1 rounded-t bg-lime/70" style={{ height: `${h}%` }} />
            ))}
          </div>
        </Frame>
      );

    case "monitoramento-ao-vivo":
      return (
        <Frame title="Monitoramento · Ao vivo">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Circle className="w-2 h-2 fill-lime text-lime animate-pulse" />
              <span className="text-xs font-semibold text-lime uppercase">Ao vivo agora</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-muted/40 p-3">
                <p className="text-[10px] text-muted-foreground uppercase">Visitantes</p>
                <p className="text-xl font-bold text-foreground">24</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3">
                <p className="text-[10px] text-muted-foreground uppercase">Hoje</p>
                <p className="text-xl font-bold text-foreground">R$ 3.4k</p>
              </div>
            </div>
            <div className="rounded-lg bg-lime/10 border border-lime/30 p-3 flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-lime" />
              <span className="text-sm text-foreground">Novo orçamento gerado</span>
            </div>
          </div>
        </Frame>
      );

    case "nota-fiscal":
      return (
        <Frame title="Nota Fiscal · Emissão">
          <div className="space-y-3">
            <div className="rounded-lg bg-muted/40 p-3 space-y-1">
              <p className="text-[10px] text-muted-foreground uppercase">Pedido</p>
              <p className="text-sm font-semibold text-foreground">Casamento · 15/03</p>
              <p className="text-xs text-muted-foreground">Cliente e CPF/CNPJ preenchidos</p>
            </div>
            <div className="rounded-lg border border-border/60 p-3">
              <Row>
                <span className="text-sm text-muted-foreground">Valor da nota</span>
                <span className="text-sm font-semibold text-foreground">R$ 8.500,00</span>
              </Row>
              <Row>
                <span className="text-sm text-muted-foreground">Atividade</span>
                <span className="text-sm text-foreground">Buffet para eventos</span>
              </Row>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-lime/10 border border-lime/30">
              <Receipt className="w-5 h-5 text-lime" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Nota autorizada</p>
                <p className="text-xs text-muted-foreground">PDF e XML salvos no pedido</p>
              </div>
              <Check className="w-4 h-4 text-lime" />
            </div>
          </div>
        </Frame>
      );

    case "mensageria-automatica":
      return (
        <Frame title="Automações · WhatsApp">
          <div className="space-y-2">
            {[
              { label: "Proposta sem resposta", when: "após 2 dias" },
              { label: "Pagamento confirmado", when: "na hora" },
              { label: "Parcela a vencer", when: "automático" },
              { label: "Pedido em preparação", when: "no status" },
              { label: "Saiu para entrega", when: "1h antes" },
              { label: "Pedido de avaliação", when: "pós-evento" },
            ].map(({ label, when }) => (
              <div key={label} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/40">
                <MessageCircle className="w-4 h-4 text-lime shrink-0" />
                <span className="text-sm text-foreground flex-1 truncate">{label}</span>
                <span className="text-[10px] text-muted-foreground">{when}</span>
                <span className="text-[10px] font-semibold text-lime">ON</span>
              </div>
            ))}
          </div>
        </Frame>
      );

    default:
      return null;
  }
};

export default SolucaoMockScreen;
